<script>
  import { onMount } from 'svelte';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';

  let compliances = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;

  onMount(async () => {
    if ($selectedParticipant) {
      fetchDeviceCompliance(1);
    }
  });

  async function fetchDeviceCompliance(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/glucoseReadings/deviceCompliance?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}`;

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch compliance');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      compliances = responseData.data;
    } catch (err) {
      console.error('Error fetching log entries:', err);
      compliances = [];
    } finally {
      isLoading = false;
    }
  }

  function handleRefresh() {
    //console.log("handleRefresh");
    fetchDeviceCompliance(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchDeviceCompliance(newPage);
    }
  }
</script>

<StudyHeader />

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<!-- Title and Filters -->
<Toolbar title="Device Compliance" itemCountTitle="Sensors" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div>
      <button on:click={handleRefresh} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing device compliance report
    </p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Visit</th>
          <th class="px-4 py-2 text-left bold">Activity</th>
          <th class="px-4 py-2 text-left bold">Sensor</th>
          <th class="px-4 py-2 text-left bold w-48">Started - Completed (Local)</th>
          <th class="px-4 py-2 text-left bold w-48">Readings From - To (Local)</th>
          <th class="px-4 py-2 text-left bold">Count</th>
          <th class="px-4 py-2 text-left bold w-32">Duration</th>
          <th class="px-4 py-2 text-left bold">Wear</th>
          <th class="px-4 py-2 text-left bold">Expected</th>
          <th class="px-4 py-2 text-left bold">Compliance</th>
        </tr>
      </thead>
      <tbody>
        {#each compliances as compliance, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{compliance.visit ?? 'n/a'}</td>
            <td class="px-4 py-2">{compliance.activity ?? 'n/a'}<br />({compliance.activityId})</td>
            <td class="px-4 py-2">{compliance.sensor}</td>
            <td class="px-4 py-2 w-52">
              {compliance.started
                ? formatToLocalDateTime(compliance.started, $selectedParticipant.timezoneCode)
                : ''}
              <br />
              {compliance.completed
                ? formatToLocalDateTime(compliance.completed, $selectedParticipant.timezoneCode)
                : 'not completed'}
              <br />
            </td>
            <td class="px-4 py-2 w-52">
              {compliance.minRead
                ? formatToLocalDateTime(compliance.minRead, $selectedParticipant.timezoneCode)
                : ''}
              <br />
              {compliance.maxRead
                ? formatToLocalDateTime(compliance.maxRead, $selectedParticipant.timezoneCode)
                : ''}
            </td>
            <td class="px-4 py-2">{compliance.count}</td>
            <td class="px-4 py-2 w-32">
              {Math.floor(compliance.daysBetween)}d
              {Math.floor((compliance.daysBetween % 1) * 24)}h
              {Math.floor((((compliance.daysBetween % 1) * 24) % 1) * 60)}m
            </td>
            <td class="px-4 py-2">{compliance.wearpercentage}%</td>
            <td class="px-4 py-2">{compliance.expectedReadings}</td>
            <td class="px-4 py-2">{compliance.countPercentage ?? '0'}%</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Add Control Buttons -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
